import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FullUser, SEUser, UserStatus } from 'types/userService';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEmployersForGroup, inviteToSelfEnroll, removeEmployer } from 'api/operations/userService';
import ModalDelegate from 'store/general/modalDelegate';
import GroupModule from '../groupModule';
import { EmployerDelegate } from './employerDelegate';

export interface EmployerFormFields extends DefaultFormFields {
  firstName: FormControl<string>;
  middleName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  telephone: FormControl<string>;
  title: FormControl<string>;
}

export const getEmployerFormFields = (): EmployerFormFields => {
  return {
    firstName: new FormControl<string>('', { validations: ['validName'], trim: true }),
    middleName: new FormControl<string>('', { validations: ['validName'], optional: true, trim: true }),
    lastName: new FormControl<string>('', { validations: ['validName'], trim: true }),
    email: new FormControl<string>('', { validations: ['isEmailValid'], trim: true }),
    telephone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
    title: new FormControl<string>('', { validations: ['validName'], optional: true }),
  };
};

interface InviteEmployersDelegateProps {
  groupModule: GroupModule;
}

export class InviteEmployersDelegate extends ModalDelegate {
  private _employers: EmployerDelegate[] = [];
  private _isInitialDataLoading = true;
  private _isLoading = false;

  private _createEmployerDelegates(employers: FullUser[]): void {
    const currentAdmin = employers.find(({ email }) => email === this.groupModule.group.contactInfo?.primaryEmail);
    const primaryEmail = this.groupModule.group.contactInfo?.primaryEmail;

    const createEmployerDelegate = (employer: FullUser, isAdmin = false): EmployerDelegate =>
      new EmployerDelegate({
        employer,
        groupId: this.groupModule.group.id,
        setIsLoading: this.setIsLoading,
        refreshData: this._getEmployersForGroup.bind(this),
        isAdmin,
      });

    this._employers =
      currentAdmin?.status === UserStatus.INVITED
        ? [
            createEmployerDelegate(currentAdmin, true),
            ...employers
              .filter(({ email }) => email !== primaryEmail)
              .map((employer) => createEmployerDelegate(employer)),
          ]
        : employers.map((employer) => createEmployerDelegate(employer));
  }

  private async _getEmployersForGroup(): Promise<void> {
    const employers = await getEmployersForGroup(this.groupModule.group.id);
    runInAction(() => {
      this._createEmployerDelegates(employers);
    });
  }

  public readonly groupModule: GroupModule;

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.groupModule.isLoading;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get employerDelegates(): EmployerDelegate[] {
    return this._employers;
  }

  public readonly addEmployerForm: FormController<EmployerFormFields> = new FormController(getEmployerFormFields(), {
    onSubmit: this.addEmployer.bind(this),
  });

  private _setIsInitialDataLoading(status: boolean): void {
    this._isInitialDataLoading = status;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getEmployersForGroup(): Promise<void> {
    this._setIsInitialDataLoading(true);
    await this._getEmployersForGroup();
    this._setIsInitialDataLoading(false);
  }

  public closeEditEmployersModal(): void {
    this.close();
    this.addEmployerForm.resetForm();
  }

  public async addEmployer(): Promise<void> {
    await runInAction(async () => {
      this.setIsLoading(true);

      const newEmployerInfo = {
        ...this.addEmployerForm.getFormDataObject(),
        groupId: this.groupModule.group.id,
        userType: 'EMPLOYER',
      };
      await inviteToSelfEnroll([newEmployerInfo as SEUser]);

      await runInAction(async () => {
        this.addEmployerForm.resetForm();
        await this._getEmployersForGroup();
        runInAction(() => {
          this.setIsLoading(false);
        });
      });
    });
  }

  public async removeEmployer(employerId: string): Promise<void> {
    this.setIsLoading(true);
    const groupId = this.groupModule.group.id;
    await removeEmployer(groupId, employerId);
    runInAction(() => {
      this._employers = this._employers.filter(({ employer }) => employer.id !== employerId);
      this.setIsLoading(false);
    });
  }

  public closeModal(): void {
    this.addEmployerForm.resetForm();
    this._employers.forEach((employerDelegate) => {
      employerDelegate.setEditModeOn(false);
      employerDelegate.editEmployerForm.resetForm();
    });
    this.close();
  }

  constructor({ groupModule }: InviteEmployersDelegateProps) {
    super();
    this.groupModule = groupModule;

    makeObservable<
      InviteEmployersDelegate,
      | '_employers'
      | '_isInitialDataLoading'
      | '_isLoading'
      | '_createEmployerDelegates'
      | '_setIsInitialDataLoading'
      | '_getEmployersForGroup'
    >(this, {
      _employers: observable,
      _isLoading: observable,
      _isInitialDataLoading: observable,

      isLoading: computed,
      isInitialDataLoading: computed,
      employerDelegates: computed,

      _getEmployersForGroup: action.bound,
      _setIsInitialDataLoading: action.bound,
      _createEmployerDelegates: action.bound,
      setIsLoading: action.bound,
      getEmployersForGroup: action.bound,
      addEmployer: action.bound,
      removeEmployer: action.bound,
      closeModal: action.bound,
    });
  }
}
